/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * THEIR `Add Set` SPLIT CONTROL — HIS OWN ASK, WITH FOUR SCREENSHOTS (2026-09-19).
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * *"ok but you see how this need to be? inside a workout. also when we hover in each button and
 * last photo when we click the plus. everfit has up to 8 sets. they're more than enough."*
 *
 * ONE rounded outlined control, two halves separated by a hairline: `Add Set` on the left adds
 * ONE, the `+` on the right opens a menu of `+ 2 Sets … + 8 Sets`. Hovering outlines the hovered
 * HALF ONLY. Read off his four stills; the CEILING is his sentence, not an inference.
 *
 * ⚠️ **THE RULES LIVE HERE AND THE COMPONENT ONLY DRAWS THEM.** The menu's contents, the ceiling
 * and what a press means are three claims a ruler can hold; a `<button>` that happens to call
 * `onAdd(3)` is not.
 *
 * ⚠️ **AND THE CEILING IS A CLAMP, NOT A LIST LENGTH.** `MAX_ADD_SETS` decides both what the menu
 * offers and what a caller may ask for, so a future caller that computes an `n` from somewhere
 * else cannot walk past it — the two-numbers-for-one-concept trap, closed before it opens.
 */

/** His sentence: *"everfit has up to 8 sets. they're more than enough."* */
export const MAX_ADD_SETS = 8;

/**
 * The menu's rows, in order.
 *
 * ⚠️ IT STARTS AT **2**, and that is not an off-by-one. The left half already adds one, so a
 * `+ 1 Set` row would be a second control for a job the control beside it does — which is the
 * duplication his own screenshots do not have either.
 */
export function addSetOptions(): readonly number[] {
  const out: number[] = [];
  for (let n = 2; n <= MAX_ADD_SETS; n += 1) out.push(n);
  return out;
}

/** What a row says. Their spelling, including the space after the `+` and the plural. */
export function addSetLabel(n: number): string {
  return `+ ${String(n)} ${n === 1 ? "Set" : "Sets"}`;
}

/**
 * How many sets a request actually adds.
 *
 * ⚠️ **A REFUSAL IS `0`, NEVER A THROW AND NEVER A SILENT `1`.** A caller asking for nonsense
 * must change nothing; adding one instead would be a control quietly doing something other than
 * what it was asked, which is how a coach comes to distrust a screen.
 */
export function setsToAdd(n: number): number {
  if (!Number.isFinite(n)) return 0;
  const whole = Math.floor(n);
  if (whole < 1) return 0;
  return Math.min(whole, MAX_ADD_SETS);
}

/**
 * WHERE THE MENU GOES — below the control or above it, and how tall it may be.
 *
 * 🔴 **THEIRS ESCAPES ITS CARD AND OURS CANNOT, AND THAT IS A FACT ABOUT THE TWO PRODUCTS RATHER
 * THAN A CHOICE.** The builder is a `<dialog>` whose body SCROLLS, so an absolutely positioned
 * menu is clipped by that scroller; their builder is a page. MEASURED in the rendered still: with
 * the control near the foot of a card, the seven rows were cut mid-way through `+ 6 Sets` — three
 * of them unreachable, with nothing on screen saying so.
 *
 * 🔴 **AND MEASURED AGAIN, THE CLIP IS TIGHTER THAN EITHER SIDE: room below 208 px, room above
 * 207 px, menu 326 px.** So "flip it up" is not an answer on its own — this returns a HEIGHT as
 * well, and the menu scrolls inside itself when neither side can hold it whole. A menu that is
 * merely moved to the other place it does not fit has been rearranged rather than repaired.
 *
 * ⚠️ **BELOW IS PREFERRED, WHICH IS HIS STILL.** Above is taken only when it is genuinely the
 * roomier side — a control that flips when it did not have to moves for no reason a coach can see.
 *
 * ⚠️ **AND THE CLIP IS THE INTERSECTION OF EVERY CLIPPING ANCESTOR, NOT THE NEAREST ONE** — the
 * caller's own note; the nearest here ended at 1022 while the box doing the cutting ended at 816.
 */
export type MenuPlacement = { below: boolean; maxHeight: number | null };

export function menuPlacement(
  control: { bottom: number; top: number },
  clip: { bottom: number; top: number },
  menuHeight: number,
): MenuPlacement {
  const gap = 4;
  const below = Math.max(0, clip.bottom - control.bottom - gap);
  const above = Math.max(0, control.top - clip.top - gap);
  if (below >= menuHeight) return { below: true, maxHeight: null };
  if (above >= menuHeight) return { below: false, maxHeight: null };
  /* Neither side holds it whole, so it takes the roomier one and scrolls inside itself.
     ⚠️ A FLOOR, because a menu two pixels tall is a menu nobody can use: below that the honest
     thing is a short scroller rather than a sliver. */
  const takeBelow = below >= above;
  return { below: takeBelow, maxHeight: Math.max(120, takeBelow ? below : above) };
}
